import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Modal, Input, List, Avatar, Badge, message, Typography, Tooltip } from 'antd';
import { MessageOutlined, SendOutlined, CustomerServiceOutlined, DeleteOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import api from '../api';
import Settings from '../../settings';

const { Text } = Typography;

const ChatWidget = () => {
    const [visible, setVisible] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [sessionId, setSessionId] = useState('');
    const [unreadCount, setUnreadCount] = useState(0);
    const [userInfo, setUserInfo] = useState({ name: 'Անանուն', age: 0 });
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [isAdminOnline, setIsAdminOnline] = useState(false);
    const [isActualAdminOnline, setIsActualAdminOnline] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const messagesEndRef = useRef(null);

    const location = useLocation();
    const { quizes, questions, settings } = useSelector((state) => state);

    const quizesRef = useRef(quizes);
    quizesRef.current = quizes;

    const questionsRef = useRef(questions);
    questionsRef.current = questions;

    const visibleRef = useRef(visible);
    visibleRef.current = visible;

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        // Global version check to force update
        if (settings && settings.app_version && settings.app_version !== Settings.app_version) {
            console.log('Version mismatch detected, clearing cache...');
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.getRegistrations().then(registrations => {
                    for (let registration of registrations) registration.unregister();
                });
            }
            if ('caches' in window) {
                caches.keys().then(names => {
                    for (let name of names) caches.delete(name);
                });
            }
        }
    }, [settings?.app_version]);

    const sendHeartbeat = useCallback(() => {
        const storedId = localStorage.getItem('chat_session_id');
        if (!storedId) return;

        const name = localStorage.getItem('chat_user_name') || 'Անանուն';
        const age = localStorage.getItem('chat_user_age') || 0;
        
        const passedQuizes = quizesRef.current || {};
        const passedCount = Object.keys(passedQuizes).length;
        const trueAnswers = Object.values(passedQuizes).reduce((sum, pts) => sum + (Number(pts) || 0), 0);
        const wrongInPassed = (passedCount * 20) - trueAnswers;
        const activeWrongs = Array.isArray(questionsRef.current) ? questionsRef.current.length : 0;
        const wrongAnswers = Math.max(0, Math.max(wrongInPassed, activeWrongs));

        api.API.post(api.Chat + '/heartbeat', { 
            name, 
            age,
            progress: {
                passed: passedCount,
                true: trueAnswers,
                wrong: wrongAnswers
            },
            current_page: window.location.pathname,
            app_version: Settings.app_version,
            push_time: Settings.push_time
        }, {
            headers: { 'X-Chat-Session-Id': storedId }
        }).catch(() => {});
    }, []);

    useEffect(() => {
        let storedId = localStorage.getItem('chat_session_id');
        if (!storedId) {
            storedId = 'user_' + Math.random().toString(36).substr(2, 9);
            localStorage.setItem('chat_session_id', storedId);
        }
        setSessionId(storedId);

        const storedName = localStorage.getItem('chat_user_name');
        const storedAge = localStorage.getItem('chat_user_age');
        if (storedName) {
            setUserInfo({ name: storedName, age: storedAge || 0 });
        }

        const updateUserInfo = () => {
            const sName = localStorage.getItem('chat_user_name');
            const sAge = localStorage.getItem('chat_user_age');
            if (sName) {
                setUserInfo({ name: sName, age: sAge || 0 });
            }
            sendHeartbeat();
        };
        window.addEventListener('user_info_updated', updateUserInfo);

        let isSubscribed = true;

        const poll = () => {
            api.API.get(api.Chat + '/poll', {
                headers: { 'X-Chat-Session-Id': storedId }
            }).then(response => {
                if (!isSubscribed || !response) return;
                const resData = response?.data || response;
                const { messages, force_show, pinned, clear, admin_online, update_app } = resData;
                
                if (clear) {
                    setMessages([]);
                    setUnreadCount(0);
                    setVisible(false);
                    return;
                }

                if (update_app) {
                    message.loading('Թարմացվում է...', 2);
                    if ('serviceWorker' in navigator) {
                        navigator.serviceWorker.getRegistrations().then(registrations => {
                            for (let registration of registrations) registration.unregister();
                        });
                    }
                    if ('caches' in window) {
                        caches.keys().then(names => {
                            for (let name of names) caches.delete(name);
                        });
                    }
                }

                setIsActualAdminOnline(!!admin_online);
                setIsAdminOnline((!!force_show || !!pinned) && !!admin_online);
                
                if (messages && messages.length > 0) {
                    setMessages(prev => {
                        const next = [...prev];
                        messages.forEach(msg => {
                            const exists = next.some(m => m.id === msg.id || (m.timestamp === msg.timestamp && m.text === msg.text));
                            if (!exists) next.push(msg);
                        });
                        return next;
                    });
                    const hasAdminMessage = messages.some(msg => msg.from === 'admin');
                    if (hasAdminMessage && !visibleRef.current) setVisible(true);
                    if (!visibleRef.current) setUnreadCount(prev => prev + messages.length);
                }
            }).catch(() => {});
        };

        // Initial poll and heartbeat
        poll();
        sendHeartbeat();

        const pollInterval = setInterval(poll, 5000);
        const heartbeatInterval = setInterval(sendHeartbeat, 20000);

        return () => {
            isSubscribed = false;
            clearInterval(pollInterval);
            clearInterval(heartbeatInterval);
            window.removeEventListener('user_info_updated', updateUserInfo);
        };
    }, [sendHeartbeat]);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    useEffect(() => {
        sendHeartbeat();
    }, [location.pathname, sendHeartbeat]);

    const handleSend = () => {
        if (!inputValue.trim() || isSending) return;
        const text = inputValue.trim();
        setIsSending(true);

        api.API.post(api.Chat + '/send', { to_session_id: 'admin', text: text }, {
            headers: { 'X-Chat-Session-Id': sessionId }
        }).then((res) => {
            setInputValue('');
            const resMsg = res?.data?.message || res?.message;
            const myMsg = { 
                id: resMsg?.id || Math.random().toString(36).substr(2, 9),
                from: sessionId, 
                text: text, 
                timestamp: resMsg?.timestamp || Math.floor(Date.now() / 1000) 
            };
            setMessages(prev => {
                if (prev.some(m => m.id === myMsg.id)) return prev;
                return [...prev, myMsg];
            });
        }).catch(err => {
            console.error('ChatWidget send error:', err);
            message.error('Չհաջողվեց ուղարկել հաղորդագրությունը');
        }).finally(() => {
            setIsSending(false);
        });
    };

    const handleClear = () => {
        setMessages([]);
        setVisible(false);
        api.API.post(api.Chat + '/close', {}, { headers: { 'X-Chat-Session-Id': sessionId } }).catch(() => {});
    };

    return (
        <>
            {(unreadCount > 0 || isAdminOnline) && !visible && (
                <div style={{ position: 'fixed', bottom: isMobile ? 20 : 30, right: isMobile ? 20 : 30, zIndex: 1000 }}>
                    <Badge count={unreadCount}>
                        <Button 
                            type="primary" 
                            shape="circle" 
                            size="large" 
                            icon={<MessageOutlined style={{ fontSize: 24 }} />} 
                            onClick={() => {
                                setVisible(true);
                                setUnreadCount(0);
                            }}
                            style={{ width: 60, height: 60, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}
                        />
                    </Badge>
                </div>
            )}

            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', paddingRight: 24 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <Avatar style={{ backgroundColor: '#1890ff' }} icon={<CustomerServiceOutlined />} />
                            <div>
                                <Text strong style={{ fontSize: 14 }}>AvtoDas Օգնական</Text>
                                <br />
                                <Text type="secondary" style={{ fontSize: 11 }}>{userInfo.name}</Text>
                            </div>
                        </div>
                        <Tooltip title="Մաքրել պատմությունը">
                            <Button 
                                type="text" 
                                danger 
                                icon={<DeleteOutlined style={{ fontSize: 13 }} />} 
                                onClick={handleClear} 
                                size="small" 
                            />
                        </Tooltip>
                    </div>
                }
                open={visible}
                visible={visible}
                onCancel={() => setVisible(false)}
                footer={null}
                width={isMobile ? '100vw' : 380}
                style={isMobile ? { 
                    position: 'fixed', 
                    bottom: 0, 
                    left: 0, 
                    right: 0, 
                    margin: 0, 
                    maxWidth: '100vw', 
                    paddingBottom: 0 
                } : { 
                    position: 'fixed', 
                    bottom: 15, 
                    right: 15, 
                    margin: 0, 
                    paddingBottom: 0 
                }}
                mask={isMobile}
                closable={true}
                className="messenger-modal"
            >
                <div style={{ height: isMobile ? '72vh' : 440, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: '8px 15px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            Բարև, {userInfo.name} {userInfo.age && userInfo.age !== 'N/A' && userInfo.age !== 0 ? `(${userInfo.age} տ․)` : ''}
                        </Text>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: '15px', background: '#f5f7f9' }}>
                        <List
                            dataSource={messages}
                            renderItem={item => {
                                const isMe = item.from === sessionId;
                                return (
                                    <div style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
                                        <div style={{ 
                                            maxWidth: '85%', 
                                            padding: '8px 14px', 
                                            borderRadius: 18, 
                                            borderTopRightRadius: isMe ? 2 : 18,
                                            borderTopLeftRadius: isMe ? 20 : 4,
                                            background: isMe ? '#1890ff' : '#fff',
                                            color: isMe ? '#fff' : '#333',
                                            boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
                                            fontSize: '14px',
                                            wordBreak: 'break-word'
                                        }}>
                                            {item.text}
                                            {item.is_broadcast && !isMe && (
                                                <div style={{ fontSize: '9px', marginTop: 4, opacity: 0.6 }}>Հայտարարություն</div>
                                            )}
                                        </div>
                                    </div>
                                );
                            }}
                        />
                        <div ref={messagesEndRef} />
                    </div>
                    <div style={{ padding: '10px 14px', borderTop: '1px solid #f0f0f0', display: 'flex', gap: 8, background: '#fff', alignItems: 'center' }}>
                        <Input 
                            placeholder="Հարցրեք..." 
                            value={inputValue} 
                            onChange={e => setInputValue(e.target.value)}
                            onPressEnter={handleSend}
                            style={{ borderRadius: 20, flex: 1, padding: '6px 14px' }}
                            disabled={isSending}
                        />
                        <Button 
                            type="primary" 
                            shape="circle" 
                            icon={<SendOutlined />} 
                            onClick={handleSend} 
                            loading={isSending}
                            disabled={!inputValue.trim() || isSending}
                            style={{ flexShrink: 0, width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center' }} 
                        />
                    </div>
                </div>
            </Modal>
            <style dangerouslySetInnerHTML={{ __html: `
                .messenger-modal .ant-modal-content {
                    border-radius: 20px !important;
                    overflow: hidden;
                    box-shadow: 0 12px 40px rgba(0,0,0,0.18) !important;
                }
                .messenger-modal .ant-modal-header { padding: 12px 18px !important; }
                .messenger-modal .ant-modal-body { padding: 0 !important; }
                @media (max-width: 767px) {
                    .messenger-modal { 
                        max-width: 100vw !important; 
                        width: 100vw !important; 
                        margin: 0 !important; 
                        top: auto !important; 
                        bottom: 0 !important; 
                        left: 0 !important;
                        right: 0 !important;
                        padding-bottom: 0 !important;
                    }
                    .messenger-modal .ant-modal {
                        max-width: 100vw !important;
                        margin: 0 !important;
                        top: auto !important;
                        bottom: 0 !important;
                    }
                    .messenger-modal .ant-modal-content { 
                        border-radius: 20px 20px 0 0 !important; 
                        border-bottom-left-radius: 0 !important;
                        border-bottom-right-radius: 0 !important;
                    }
                }
            `}} />
        </>
    );
};

export default ChatWidget;
